import { useRef, useCallback, useState, useEffect } from 'react';
import {
  Search,
  Calendar,
  X,
  BookOpen,
  PlayCircle,
  AlertTriangle,
  HelpCircle,
  Lightbulb,
  Info,
  CheckCircle,
  AlertCircle,
  ExternalLink,
  Clock,
  ChevronRight,
  Mail,
  MessageSquare,
  Route,
  Sparkles,
} from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { useHelp } from './useHelp';
import { useTour } from '@/components/tour';
import { HelpFAQList } from './HelpFAQList';
import { HelpSectionNav } from './HelpSectionNav';
import { HelpRelatedLinks } from './HelpRelatedLinks';
import type { HelpTip, HelpWalkthrough, HelpVideo, HelpTroubleshooting } from '@/content/help/types';
import type { HelpContent } from '@/content/help';
import { searchAllHelpContent } from '@/content/help';
import { useChat } from '@/components/chat/ChatContext';
import { formatDuration, formatLastUpdated } from '@/content/help/utils';

// ============================================================================
// HELPER COMPONENTS
// ============================================================================

function highlightText(text: string, query: string): React.ReactNode {
  if (!query.trim()) return text;

  const parts = text.split(new RegExp(`(${escapeRegExp(query)})`, 'gi'));
  return parts.map((part, index) =>
    part.toLowerCase() === query.toLowerCase() ? (
      <mark key={index} className="bg-yellow-200 dark:bg-yellow-800/40 rounded px-0.5">
        {part}
      </mark>
    ) : (
      part
    )
  );
}

function escapeRegExp(string: string): string {
  return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/** Tip/Alert component */
function HelpTipCard({ tip }: { tip: HelpTip }) {
  const styles = {
    info: {
      bg: 'bg-blue-50 border-blue-200 dark:bg-blue-950/30 dark:border-blue-800',
      icon: <Info className="h-4 w-4 text-blue-600 dark:text-blue-400" />,
      title: 'text-blue-800 dark:text-blue-200 dark:text-blue-300',
    },
    warning: {
      bg: 'bg-amber-50 border-amber-200 dark:bg-amber-950/30 dark:border-amber-800',
      icon: <AlertCircle className="h-4 w-4 text-amber-600 dark:text-amber-400" />,
      title: 'text-amber-800 dark:text-amber-200 dark:text-amber-300',
    },
    success: {
      bg: 'bg-green-50 border-green-200 dark:bg-green-950/30 dark:border-green-800',
      icon: <CheckCircle className="h-4 w-4 text-green-600 dark:text-green-400" />,
      title: 'text-green-800 dark:text-green-200 dark:text-green-300',
    },
    'pro-tip': {
      bg: 'bg-purple-50 border-purple-200 dark:bg-purple-950/30 dark:border-purple-800',
      icon: <Lightbulb className="h-4 w-4 text-purple-600 dark:text-purple-400" />,
      title: 'text-purple-800 dark:text-purple-200 dark:text-purple-300',
    },
  };

  const style = styles[tip.type];

  return (
    <div className={`p-3 rounded-lg border ${style.bg}`}>
      <div className="flex items-start gap-2">
        <div className="mt-0.5">{style.icon}</div>
        <div>
          <p className={`text-sm font-medium ${style.title}`}>{tip.title}</p>
          <p className="text-sm text-muted-foreground mt-0.5">{tip.content}</p>
        </div>
      </div>
    </div>
  );
}

/** Walkthrough/Guide card */
function WalkthroughCard({ walkthrough }: { walkthrough: HelpWalkthrough }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="border border-border rounded-lg overflow-hidden">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full p-3 flex items-center justify-between hover:bg-muted transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className="h-8 w-8 rounded-full bg-primary-100 flex items-center justify-center">
            <BookOpen className="h-4 w-4 text-primary-600" />
          </div>
          <div className="text-left">
            <p className="text-sm font-medium text-foreground">{walkthrough.title}</p>
            <p className="text-xs text-muted-foreground">{walkthrough.steps.length} steps</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {walkthrough.estimatedMinutes && (
            <Badge variant="secondary" className="text-xs">
              <Clock className="h-3 w-3 mr-1" />
              {walkthrough.estimatedMinutes}m
            </Badge>
          )}
          <ChevronRight
            className={`h-4 w-4 text-muted-foreground transition-transform ${expanded ? 'rotate-90' : ''}`}
          />
        </div>
      </button>
      {expanded && (
        <div className="px-3 pb-3 border-t border-border">
          <p className="text-sm text-muted-foreground py-2">{walkthrough.description}</p>
          <ol className="space-y-2">
            {walkthrough.steps.map((step) => (
              <li key={step.stepNumber} className="flex gap-3">
                <span className="flex-shrink-0 h-6 w-6 rounded-full bg-muted flex items-center justify-center text-xs font-medium text-muted-foreground">
                  {step.stepNumber}
                </span>
                <div>
                  <p className="text-sm font-medium text-foreground">{step.title}</p>
                  <p className="text-xs text-muted-foreground">{step.description}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      )}
    </div>
  );
}

/** Video card */
function VideoCard({ video }: { video: HelpVideo }) {
  return (
    <a
      href={video.videoUrl}
      target="_blank"
      rel="noopener noreferrer"
      className="block border border-border rounded-lg overflow-hidden hover:border-primary-300 hover:shadow-sm transition-all"
    >
      <div className="aspect-video bg-muted flex items-center justify-center relative">
        {video.thumbnailUrl ? (
          <img
            src={video.thumbnailUrl}
            alt={video.title}
            className="w-full h-full object-cover"
          />
        ) : (
          <PlayCircle className="h-12 w-12 text-muted-foreground" />
        )}
        {video.durationSeconds && (
          <Badge className="absolute bottom-2 right-2 text-xs bg-black/70">
            {formatDuration(video.durationSeconds)}
          </Badge>
        )}
      </div>
      <div className="p-3">
        <p className="text-sm font-medium text-foreground flex items-center gap-1">
          {video.title}
          <ExternalLink className="h-3 w-3 text-muted-foreground" />
        </p>
        <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{video.description}</p>
      </div>
    </a>
  );
}

/** Troubleshooting item */
function TroubleshootingCard({ item }: { item: HelpTroubleshooting }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="border border-border rounded-lg overflow-hidden">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full p-3 flex items-start gap-3 hover:bg-muted transition-colors text-left"
      >
        <AlertTriangle className="h-4 w-4 text-amber-500 mt-0.5 flex-shrink-0" />
        <div className="flex-1">
          <p className="text-sm font-medium text-foreground">{item.problem}</p>
        </div>
        <ChevronRight
          className={`h-4 w-4 text-muted-foreground transition-transform mt-0.5 ${expanded ? 'rotate-90' : ''}`}
        />
      </button>
      {expanded && (
        <div className="px-3 pb-3 border-t border-border space-y-3">
          <div>
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mt-2">
              Possible Causes
            </p>
            <ul className="mt-1 space-y-1">
              {item.possibleCauses.map((cause, i) => (
                <li key={i} className="text-sm text-muted-foreground flex items-start gap-2">
                  <span className="text-muted-foreground">•</span>
                  {cause}
                </li>
              ))}
            </ul>
          </div>
          <div>
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
              Solutions
            </p>
            {item.solutions.map((solution, i) => (
              <div key={i} className="mt-2">
                <p className="text-sm font-medium text-foreground">{solution.description}</p>
                {solution.steps && (
                  <ol className="mt-1 space-y-1 pl-4">
                    {solution.steps.map((step, j) => (
                      <li key={j} className="text-sm text-muted-foreground list-decimal">
                        {step}
                      </li>
                    ))}
                  </ol>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// MAIN PANEL COMPONENT
// ============================================================================

export function HelpPanel() {
  const { isOpen, content, searchQuery, closeHelp, setSearchQuery, openHelp } = useHelp();
  const { hasTourForCurrentRoute, startTourForCurrentRoute } = useTour();
  const { openChatWithMessage } = useChat();
  const sectionRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const [activeTab, setActiveTab] = useState('overview');
  const [crossPageResults, setCrossPageResults] = useState<HelpContent[]>([]);

  const handleTakeTour = useCallback(() => {
    closeHelp();
    // Wait for the Sheet close animation, then start tour
    setTimeout(() => startTourForCurrentRoute(), 300);
  }, [closeHelp, startTourForCurrentRoute]);

  const handleSectionClick = useCallback((sectionId: string) => {
    const sectionElement = sectionRefs.current[sectionId];
    if (sectionElement) {
      sectionElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, []);

  const setSectionRef = useCallback(
    (sectionId: string) => (el: HTMLDivElement | null) => {
      sectionRefs.current[sectionId] = el;
    },
    []
  );

  // Cross-page search: find help content from other pages
  useEffect(() => {
    if (searchQuery.trim().length >= 2) {
      const allMatches = searchAllHelpContent(searchQuery);
      const filtered = allMatches.filter((item) => item.id !== content?.id);
      setCrossPageResults(filtered);
    } else {
      setCrossPageResults([]);
    }
  }, [searchQuery, content?.id]);

  // Filter sections based on search query
  const filteredSections = content?.sections.filter(
    (section) =>
      !searchQuery.trim() ||
      section.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      section.content.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Check if content has various types
  const hasWalkthroughs = content?.walkthroughs && content.walkthroughs.length > 0;
  const hasVideos = content?.videos && content.videos.length > 0;
  const hasTroubleshooting = content?.troubleshooting && content.troubleshooting.length > 0;
  const hasFaqs = content?.faqs && content.faqs.length > 0;
  const hasTips = content?.tips && content.tips.length > 0;

  // Determine available tabs
  const availableTabs = [
    { id: 'overview', label: 'Overview', icon: BookOpen },
    ...(hasWalkthroughs ? [{ id: 'guides', label: 'Guides', icon: BookOpen }] : []),
    ...(hasVideos ? [{ id: 'videos', label: 'Videos', icon: PlayCircle }] : []),
    ...(hasTroubleshooting ? [{ id: 'troubleshoot', label: 'Issues', icon: AlertTriangle }] : []),
    ...(hasFaqs ? [{ id: 'faqs', label: 'FAQs', icon: HelpCircle }] : []),
  ];

  const hasSearchResults =
    !searchQuery.trim() ||
    (filteredSections && filteredSections.length > 0) ||
    content?.faqs.some(
      (faq) =>
        faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
        faq.answer.toLowerCase().includes(searchQuery.toLowerCase()) ||
        faq.tags.some((tag) =>
          tag.toLowerCase().includes(searchQuery.toLowerCase())
        )
    ) ||
    crossPageResults.length > 0;

  return (
    <Sheet open={isOpen} onOpenChange={(open) => !open && closeHelp()}>
      <SheetContent side="right" className="w-full sm:max-w-md p-0 flex flex-col">
        {/* Header */}
        <SheetHeader className="px-6 pt-6 pb-4 border-b border-border flex-shrink-0">
          <div className="flex items-center justify-between">
            <div>
              <SheetTitle className="text-lg font-semibold text-foreground">
                {content?.title || 'Help'}
              </SheetTitle>
              <SheetDescription className="text-sm text-muted-foreground mt-1">
                {content?.description || 'Get help with this page'}
              </SheetDescription>
            </div>
          </div>

          {/* Search Input */}
          <div className="relative mt-4">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              type="search"
              placeholder="Search help..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 pr-9 bg-muted border-border"
            />
            {searchQuery && (
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setSearchQuery('')}
                className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 text-muted-foreground hover:text-muted-foreground"
              >
                <X className="h-4 w-4" />
              </Button>
            )}
          </div>
        </SheetHeader>

        {/* Take a Tour button */}
        {hasTourForCurrentRoute && (
          <div className="px-6 py-3 border-b border-border flex-shrink-0">
            <Button
              variant="outline"
              className="w-full justify-start gap-2 text-sm"
              onClick={handleTakeTour}
            >
              <Route className="h-4 w-4 text-indigo-500" />
              Take a Tour
            </Button>
          </div>
        )}

        {/* Scrollable Content */}
        <ScrollArea className="flex-1">
          <div className="px-6 py-4">
            {!content ? (
              <p className="text-sm text-muted-foreground italic">
                No help content available for this page.
              </p>
            ) : !hasSearchResults ? (
              <div className="py-8 text-center space-y-3">
                <p className="text-sm text-muted-foreground">
                  No results found for "{searchQuery}"
                </p>
                <div className="flex items-center justify-center gap-2">
                  <Button
                    variant="link"
                    size="sm"
                    onClick={() => setSearchQuery('')}
                  >
                    Clear search
                  </Button>
                  <Button
                    variant="default"
                    size="sm"
                    onClick={() => {
                      closeHelp();
                      openChatWithMessage(searchQuery);
                    }}
                    className="gap-1.5"
                  >
                    <Sparkles className="h-3.5 w-3.5" />
                    Ask AI
                  </Button>
                </div>
              </div>
            ) : (
              <>
                {/* Tips at the top */}
                {hasTips && !searchQuery.trim() && (
                  <div className="space-y-2 mb-4">
                    {content.tips!.map((tip) => (
                      <HelpTipCard key={tip.id} tip={tip} />
                    ))}
                  </div>
                )}

                {/* Tabs for different content types */}
                {availableTabs.length > 1 && !searchQuery.trim() ? (
                  <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                    <TabsList className="w-full justify-start mb-4 h-auto flex-wrap gap-1 bg-transparent p-0">
                      {availableTabs.map((tab) => (
                        <TabsTrigger
                          key={tab.id}
                          value={tab.id}
                          className="data-[state=active]:bg-primary-50 data-[state=active]:text-primary-700 text-xs px-3 py-1.5 rounded-full border border-transparent data-[state=active]:border-primary-200"
                        >
                          {tab.label}
                        </TabsTrigger>
                      ))}
                    </TabsList>

                    {/* Overview Tab */}
                    <TabsContent value="overview" className="mt-0 space-y-6">
                      {/* Section Navigation */}
                      {content.sections.length > 1 && (
                        <HelpSectionNav
                          sections={content.sections}
                          onSectionClick={handleSectionClick}
                        />
                      )}

                      {/* Sections */}
                      {content.sections.map((section) => (
                        <div
                          key={section.id}
                          ref={setSectionRef(section.id)}
                          className="scroll-mt-4"
                        >
                          <h3 className="text-sm font-semibold text-foreground mb-2">
                            {section.title}
                          </h3>
                          <div
                            className="text-sm text-muted-foreground prose prose-sm prose-slate max-w-none
                              [&_ul]:list-disc [&_ul]:pl-4 [&_ul]:space-y-1 [&_ul]:my-2
                              [&_li]:text-muted-foreground [&_strong]:text-foreground [&_strong]:font-medium
                              [&_p]:my-2"
                            dangerouslySetInnerHTML={{ __html: section.content }}
                          />
                        </div>
                      ))}

                      {/* Related Links */}
                      <HelpRelatedLinks links={content.relatedLinks} />
                    </TabsContent>

                    {/* Guides Tab */}
                    {hasWalkthroughs && (
                      <TabsContent value="guides" className="mt-0 space-y-3">
                        <p className="text-sm text-muted-foreground mb-4">
                          Step-by-step guides to help you get things done.
                        </p>
                        {content.walkthroughs!.map((walkthrough) => (
                          <WalkthroughCard key={walkthrough.id} walkthrough={walkthrough} />
                        ))}
                      </TabsContent>
                    )}

                    {/* Videos Tab */}
                    {hasVideos && (
                      <TabsContent value="videos" className="mt-0">
                        <p className="text-sm text-muted-foreground mb-4">
                          Video tutorials and demonstrations.
                        </p>
                        <div className="grid gap-3">
                          {content.videos!.map((video) => (
                            <VideoCard key={video.id} video={video} />
                          ))}
                        </div>
                      </TabsContent>
                    )}

                    {/* Troubleshooting Tab */}
                    {hasTroubleshooting && (
                      <TabsContent value="troubleshoot" className="mt-0 space-y-3">
                        <p className="text-sm text-muted-foreground mb-4">
                          Common issues and how to resolve them.
                        </p>
                        {content.troubleshooting!.map((item) => (
                          <TroubleshootingCard key={item.id} item={item} />
                        ))}
                      </TabsContent>
                    )}

                    {/* FAQs Tab */}
                    {hasFaqs && (
                      <TabsContent value="faqs" className="mt-0">
                        <p className="text-sm text-muted-foreground mb-4">
                          Frequently asked questions.
                        </p>
                        <HelpFAQList faqs={content.faqs} searchQuery={searchQuery} />
                      </TabsContent>
                    )}
                  </Tabs>
                ) : (
                  /* No tabs - show all content (or search results) */
                  <>
                    {/* Section Navigation */}
                    {!searchQuery.trim() && content.sections.length > 1 && (
                      <HelpSectionNav
                        sections={content.sections}
                        onSectionClick={handleSectionClick}
                      />
                    )}

                    {/* "From this page" heading when searching */}
                    {searchQuery.trim() && (filteredSections && filteredSections.length > 0 || content.faqs.some(
                      (faq) =>
                        faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
                        faq.answer.toLowerCase().includes(searchQuery.toLowerCase()) ||
                        faq.tags.some((tag) => tag.toLowerCase().includes(searchQuery.toLowerCase()))
                    )) && (
                      <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-3">
                        From this page
                      </p>
                    )}

                    {/* Sections */}
                    {filteredSections && filteredSections.length > 0 && (
                      <div className="space-y-6 mb-6">
                        {filteredSections.map((section) => (
                          <div
                            key={section.id}
                            ref={setSectionRef(section.id)}
                            className="scroll-mt-4"
                          >
                            <h3 className="text-sm font-semibold text-foreground mb-2">
                              {highlightText(section.title, searchQuery)}
                            </h3>
                            <div
                              className="text-sm text-muted-foreground prose prose-sm prose-slate max-w-none
                                [&_ul]:list-disc [&_ul]:pl-4 [&_ul]:space-y-1 [&_ul]:my-2
                                [&_li]:text-muted-foreground [&_strong]:text-foreground [&_strong]:font-medium
                                [&_p]:my-2"
                              dangerouslySetInnerHTML={{
                                __html: searchQuery.trim()
                                  ? section.content.replace(
                                      new RegExp(`(${escapeRegExp(searchQuery)})`, 'gi'),
                                      '<mark class="bg-yellow-200 dark:bg-yellow-800/40 rounded px-0.5">$1</mark>'
                                    )
                                  : section.content,
                              }}
                            />
                          </div>
                        ))}
                      </div>
                    )}

                    {/* FAQs */}
                    {content.faqs.length > 0 && (
                      <div>
                        <h3 className="text-sm font-semibold text-foreground mb-3">
                          Frequently Asked Questions
                        </h3>
                        <HelpFAQList faqs={content.faqs} searchQuery={searchQuery} />
                      </div>
                    )}

                    {/* Cross-page results */}
                    {searchQuery.trim() && crossPageResults.length > 0 && (
                      <div className="mt-6 pt-4 border-t border-border">
                        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-3">
                          From other pages
                        </p>
                        <div className="space-y-2">
                          {crossPageResults.map((result) => (
                            <button
                              key={result.id}
                              onClick={() => openHelp(result.id)}
                              className="w-full text-left p-3 rounded-lg border border-border hover:bg-muted transition-colors"
                            >
                              <p className="text-sm font-medium text-foreground">
                                {highlightText(result.title, searchQuery)}
                              </p>
                              <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">
                                {result.description}
                              </p>
                            </button>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Related Links */}
                    {!searchQuery.trim() && (
                      <HelpRelatedLinks links={content.relatedLinks} />
                    )}
                  </>
                )}
              </>
            )}
          </div>
        </ScrollArea>

        {/* Footer */}
        {content && (
          <div className="px-6 py-3 border-t border-border flex-shrink-0">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <Calendar className="h-3.5 w-3.5" />
                <span>Updated {formatLastUpdated(content.lastUpdated)}</span>
              </div>
              {content.supportContact && (
                <div className="flex items-center gap-2">
                  {content.supportContact.email && (
                    <a
                      href={`mailto:${content.supportContact.email}`}
                      className="text-xs text-muted-foreground hover:text-primary-600 flex items-center gap-1"
                    >
                      <Mail className="h-3 w-3" />
                      Contact
                    </a>
                  )}
                  {content.supportContact.slackChannel && (
                    <span className="text-xs text-muted-foreground flex items-center gap-1">
                      <MessageSquare className="h-3 w-3" />
                      {content.supportContact.slackChannel}
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
